<template>
    <v-dialog v-model="dialog" max-width="520" :persistent="loading">
        <template v-slot:activator="{ props }">
            <v-btn v-if="canCreateUser" v-bind="props" icon size="small" color="success" variant="flat"
                class="new-user-trigger" aria-label="Create portal account" title="Create portal account">
                <v-icon>mdi-plus</v-icon>
            </v-btn>
        </template>

        <v-card class="new-user-dialog">
            <v-form ref="form" v-model="valid" @submit.prevent="save">
                <div class="new-user-dialog__header">
                    <div class="new-user-dialog__title-group">
                        <div class="new-user-dialog__icon-tile">
                            <v-icon>mdi-account-plus-outline</v-icon>
                        </div>

                        <div>
                            <div class="new-user-dialog__eyebrow">{{ $t('text.users') }}</div>
                            <h3 class="new-user-dialog__title mb-1">New portal account</h3>
                            <p class="new-user-dialog__copy mb-0">Create sign-in access for this customer.</p>
                        </div>
                    </div>

                    <v-btn v-if="!loading" icon variant="text" size="small" aria-label="Close" @click="close()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>

                <v-divider></v-divider>

                <v-card-text v-if="newuser" class="new-user-dialog__body">
                    <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-4">
                        Failed to create user. {{ error }}
                    </v-alert>

                    <v-text-field v-model.trim="newuser.Name" label="Name" :rules="required" variant="outlined"
                        density="compact" autocomplete="name" class="mb-1">
                    </v-text-field>

                    <EmailField v-model="newuser.EmailAdress"></EmailField>

                    <div class="new-user-dialog__field-label">Account type</div>
                    <v-btn-toggle v-model="newuser.UserTypeId" mandatory class="new-user-dialog__type-toggle">
                        <v-btn v-for="userType in userTypes" :key="userType.Id" :value="userType.Id"
                            :color="newuser.UserTypeId === userType.Id ? 'primary' : 'grey'"
                            :variant="newuser.UserTypeId === userType.Id ? 'flat' : 'outlined'"
                            class="new-user-dialog__type-option">
                            <v-icon class="mr-2">{{ userType.Icon }}</v-icon>
                            {{ userType.Name }}
                        </v-btn>
                    </v-btn-toggle>
                </v-card-text>

                <v-card-actions class="new-user-dialog__actions">
                    <v-btn v-if="!loading" variant="text" color="error" @click="close()">
                        {{ $t('text.close') }}
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn type="submit" :loading="loading" color="primary" variant="flat"
                        :disabled="!valid || !currentCustomerId">
                        <v-icon class="mr-2">mdi-content-save-outline</v-icon>
                        {{ $t('text.save') }}
                    </v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </v-dialog>
</template>


<script>
import http from "@/api/client";
import AuthStoreMixin from "@/helpers/mixins/AuthStoreMixin";
import EmailField from "../Inputs/EmailField.vue";

export default {
    name: 'NewUserDialog',
    props: {},
    mixins: [AuthStoreMixin],
    components: {
        EmailField,
    },

    data() {
        return {
            dialog: false,
            loading: false,
            valid: false,
            error: null,
            newuser: null,
            userTypes: [
                { Id: 4, Name: 'Moderator', Icon: 'mdi-shield-account-outline' },
                { Id: 6, Name: 'User', Icon: 'mdi-account-outline' },
            ],
            required: [val => (val || '').length > 0 || 'This field is required'],
        };
    },

    created() {
    },
    methods: {
        close() {
            this.dialog = false;
        },

        getErrorMessage(error) {
            const responseData = error?.response?.data;
            if (typeof responseData === 'string') {
                return responseData;
            }

            return responseData?.Message || responseData?.message || error?.message || 'Please try again.';
        },

        async save() {
            if (!this.currentCustomerId || !this.newuser) {
                return;
            }

            const validationResult = await this.$refs.form?.validate?.();
            if (validationResult === false || validationResult?.valid === false) {
                return;
            }

            this.error = null;
            this.loading = true;
            await http()
                .post("api2/customer/User/add", this.newuser)
                .then(() => {
                    this.close();
                    this.$emit('refresh');
                })
                .catch((error) => {
                    console.error(error);
                    this.error = this.getErrorMessage(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },

    computed: {
        userInfo() {
            return this.authUserInfo;
        },

        canCreateUser() {
            return this.userInfo?.UserTypeId != 6;
        },

        currentCustomerId() {
            return this.userInfo?.Customer?.Id ?? this.authCustomer?.Id ?? null;
        },
    },

    watch: {
        dialog(val) {
            if (val) {
                this.error = null;
                this.newuser = {
                    Name: '',
                    EmailAdress: '',
                    UserTypeId: 4,
                    CustomerId: this.currentCustomerId,
                    Active: true,
                };
            }

            if (!val) {
                this.newuser = null;
            }
        }
    },
};
</script>


<style lang="scss" scoped>
.new-user-trigger {
    min-width: 32px;
}

.new-user-dialog {
    border-radius: 8px;
    overflow: hidden;
}

.new-user-dialog__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 20px;
}

.new-user-dialog__title-group {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
}

.new-user-dialog__icon-tile {
    display: grid;
    flex: 0 0 auto;
    place-items: center;
    width: 42px;
    height: 42px;
    border-radius: 8px;
    background: var(--v-primary-base, #1976d2);
    color: #ffffff;
}

.new-user-dialog__eyebrow,
.new-user-dialog__field-label {
    color: var(--v-primary-base, #1976d2);
    font-size: 0.74rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.new-user-dialog__title {
    color: rgba(0, 0, 0, 0.87);
    font-size: 1.18rem;
    line-height: 1.2;
}

.new-user-dialog__copy {
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.9rem;
}

.new-user-dialog__body {
    padding: 20px;
}

.new-user-dialog__field-label {
    margin: 6px 0 8px;
}

.new-user-dialog__type-toggle {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    width: 100%;
}

.new-user-dialog__type-option {
    min-height: 42px;
    text-transform: none;
}

.new-user-dialog__actions {
    padding: 12px 20px 18px;
}

@media (max-width: 600px) {
    .new-user-dialog__header,
    .new-user-dialog__body,
    .new-user-dialog__actions {
        padding-left: 16px;
        padding-right: 16px;
    }

    .new-user-dialog__type-toggle {
        grid-template-columns: 1fr;
    }
}
</style>